import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { GLYPHS } from '@/components/ui';
import { KOREAN_MONTHS, addDate } from '../utils/dateUtils';

interface MonthYearSelectorProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

const MonthYearSelector: React.FC<MonthYearSelectorProps> = ({ selectedDate, onDateChange }) => {
  const [isMonthDialogOpen, setIsMonthDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const currentYear = selectedDate.getFullYear();
  const currentMonth = selectedDate.getMonth() + 1;

  // 년도 변경
  const changeYear = (direction: 'prev' | 'next') => {
    const newDate = addDate(selectedDate, direction === 'prev' ? -1 : 1, 'years');
    onDateChange(newDate);
  };

  // 월 변경
  const changeMonth = (month: number) => {
    const newDate = new Date(currentYear, month - 1, 1);
    onDateChange(newDate);
    setIsMonthDialogOpen(false);
  };

  // 좌우 화살표로 월 변경
  const changeMonthByArrow = (direction: 'prev' | 'next') => {
    let newMonth = currentMonth;
    let newYear = currentYear;
    
    if (direction === 'prev') {
      if (newMonth === 1) {
        newMonth = 12;
        newYear = currentYear - 1;
      } else {
        newMonth = newMonth - 1;
      }
    } else {
      if (newMonth === 12) {
        newMonth = 1;
        newYear = currentYear + 1;
      } else {
        newMonth = newMonth + 1;
      }
    }
    
    const newDate = new Date(newYear, newMonth - 1, 1);
    onDateChange(newDate);
  };

  // 월 선택 모달 열기
  const openMonthDialog = () => {
    setIsMonthDialogOpen(true);
  };

  // 월 선택 모달 닫기
  const closeMonthDialog = () => {
    setIsMonthDialogOpen(false);
  };

  return (
    <>
      {/* 년도/월 선택기 */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          background: 'var(--bg-base)',
          borderRadius: 'var(--r-16)',
          border: '1px solid var(--border-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {/* 월 이전 버튼 */}
        <IconButton
          type="button"
          aria-label="이전 달"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            changeMonthByArrow('prev');
          }}
          sx={{
            color: 'var(--c-cool-900)',
            '&:hover': {
              backgroundColor: 'rgba(66, 66, 66, 0.1)',
            },
          }}
        >
          <span style={{ fontSize: 22, lineHeight: 1 }}>{GLYPHS.prev}</span>
        </IconButton>

        {/* 년도 표시 및 월 선택 버튼 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openMonthDialog();
          }}
        >
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{
              fontWeight: 'bold',
              color: 'var(--c-cool-900)',
              textAlign: 'center',
            }}
          >
            {currentYear}년
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                color: 'var(--c-cool-900)',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {currentMonth}월
            </Typography>
          </Box>
        </Box>

        {/* 월 다음 버튼 */}
        <IconButton
          type="button"
          aria-label="다음 달"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            changeMonthByArrow('next');
          }}
          sx={{
            color: 'var(--c-cool-900)',
            '&:hover': {
              backgroundColor: 'rgba(66, 66, 66, 0.1)',
            },
          }}
        >
          <span style={{ fontSize: 22, lineHeight: 1 }}>{GLYPHS.next}</span>
        </IconButton>
      </Paper>

              {/* 월 선택 모달 */}
        <Dialog
          open={isMonthDialogOpen}
          onClose={closeMonthDialog}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 'var(--r-16)',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: 'center',
              color: 'var(--c-cool-900)',
              fontWeight: 'bold',
              fontSize: '1.5rem',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <IconButton
                type="button"
                aria-label="이전 연도"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  changeYear('prev');
                }}
                sx={{
                  color: 'var(--c-cool-900)',
                  '&:hover': {
                    backgroundColor: 'rgba(66, 66, 66, 0.1)',
                  },
                }}
              >
                <span style={{ fontSize: 22, lineHeight: 1 }}>{GLYPHS.prev}</span>
              </IconButton>
              {currentYear}년 월 선택
              <IconButton
                type="button"
                aria-label="다음 연도"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  changeYear('next');
                }}
                sx={{
                  color: 'var(--c-cool-900)',
                  '&:hover': {
                    backgroundColor: 'rgba(66, 66, 66, 0.1)',
                  },
                }}
              >
                <span style={{ fontSize: 22, lineHeight: 1 }}>{GLYPHS.next}</span>
              </IconButton>
            </Box>
          </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {KOREAN_MONTHS.map((month, index) => (
              <Grid item xs={3} key={index}>
                <Button
                  type="button"
                  fullWidth
                  variant={currentMonth === index + 1 ? 'contained' : 'outlined'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    changeMonth(index + 1);
                  }}
                  sx={{
                    height: '60px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    backgroundColor: currentMonth === index + 1 ? 'var(--c-cool-900)' : 'transparent',
                    color: currentMonth === index + 1 ? 'var(--fg-on-primary)' : 'var(--c-cool-900)',
                    borderColor: 'var(--c-cool-900)',
                    borderWidth: 2,
                    '&:hover': {
                      backgroundColor: currentMonth === index + 1 ? 'var(--c-cool-800)' : 'rgba(66, 66, 66, 0.04)',
                      borderColor: 'var(--c-cool-900)',
                    },
                  }}
                >
                  {month}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MonthYearSelector;
